import React from 'react'
import { ScrollView, StyleSheet, View, TouchableOpacity } from 'react-native'
import { useTheme } from '@react-navigation/native'
import { Icon, Text } from '@components'
import { Theme } from '@theme'
import { openUrl } from '@utils'
import { useScreen } from '@hooks'
import { useLanguage } from '@context'

export type SocialNetworkSectionProps = {
  isSmall?: boolean
  isList?: boolean
  color?: string
  facebook?: string
  instagram?: string
  twitter?: string
  linkedin?: string
  onPress?: () => void
  /** When set, clicking a gray (empty) icon searches the person on that network */
  searchName?: string
}

const SEARCH_URLS: Record<string, (name: string) => string> = {
  linkedin: (name) =>
    `https://www.linkedin.com/search/results/all/?keywords=${encodeURIComponent(name)}`,
  facebook: (name) => `https://www.facebook.com/search/top/?q=${encodeURIComponent(name)}`,
  instagram: (name) =>
    `https://www.instagram.com/explore/search/keyword/?q=${encodeURIComponent(name)}`,
  twitter: (name) => `https://twitter.com/search?q=${encodeURIComponent(name)}&src=typed_query`,
}

export const SocialNetworkSection = ({
  isSmall = false,
  isList = false,
  color,
  facebook,
  instagram,
  twitter,
  linkedin,
  onPress,
  searchName,
}: SocialNetworkSectionProps): React.ReactElement => {
  const theme = useTheme()
  const { t } = useLanguage()
  const styles = getStyle(theme)
  const { isDesktop } = useScreen()

  const networks = [
    { name: 'linkedin', text: t('socialNetwork.contactViaLinkedin'), url: linkedin },
    { name: 'facebook', text: t('socialNetwork.contactViaFacebook'), url: facebook },
    { name: 'instagram', text: t('socialNetwork.contactViaInstagram'), url: instagram },
    { name: 'twitter', text: t('socialNetwork.contactViaTwitter'), url: twitter },
  ]

  const STEP = isDesktop ? 25 : 15

  const hasSocialNetworks = networks.filter((n) => !!n.url).length > 0

  const handleIconPress = (network: (typeof networks)[0]) => {
    if (network.url) {
      openUrl(network.url)
    } else if (searchName) {
      const searchUrl = SEARCH_URLS[network.name]?.(searchName)
      if (searchUrl) openUrl(searchUrl)
    }
  }

  const useOuterPress = !!onPress && !isDesktop
  return (
    <TouchableOpacity
      disabled={!hasSocialNetworks && !searchName}
      style={[
        isList
          ? styles.containerList
          : isSmall
            ? [styles.containerSmall, useOuterPress && { paddingHorizontal: 0, paddingVertical: 5 }]
            : styles.container,
      ]}
      onPress={useOuterPress ? onPress : undefined}
    >
      {(() => {
        const renderedIcons = networks.map((n, index) => (
          <TouchableOpacity
            key={n.name}
            style={[
              isList
                ? styles.socialButtonList
                : isSmall
                  ? styles.socialButtonSmall
                  : styles.socialButton,
              useOuterPress && {
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: index * STEP,
              },
            ]}
            disabled={useOuterPress}
            onPress={() => handleIconPress(n)}
            activeOpacity={0.8}
            testID={`social-network-${n.name}`}
          >
            <Icon
              name={n.name as 'facebook' | 'instagram' | 'twitter' | 'linkedin'}
              size={isList ? 20 : isSmall ? 20 : 40}
              color={n.url ? color || theme.colors.primary : searchName ? theme.colors.secondary : theme.colors.greyBlue}
            />
            {isList && (
              <Text type="body" color={n.url ? undefined : theme.colors.greyBlue}>
                {n.text}
              </Text>
            )}
          </TouchableOpacity>
        ))
        // Small variant in a row that can be squeezed by a long sibling label
        // (e.g. "Aucune interaction pour l'instant") — scroll horizontally so
        // every network stays reachable instead of being clipped.
        if (isSmall && !useOuterPress) {
          return (
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={styles.buttonsContainerSmall}
            >
              {renderedIcons}
            </ScrollView>
          )
        }
        return (
          <View
            style={
              isList
                ? styles.buttonsContainerList
                : isSmall
                  ? [
                      styles.buttonsContainerSmall,
                      useOuterPress && { height: 20, width: 20 + (networks.length - 1) * STEP },
                    ]
                  : styles.buttonsContainer
            }
          >
            {renderedIcons}
          </View>
        )
      })()}
    </TouchableOpacity>
  )
}

const getStyle = (theme: Theme) =>
  StyleSheet.create({
    container: {
      paddingHorizontal: theme.spacing.xl,
      paddingVertical: theme.spacing.lg,
      alignItems: 'center',
    },
    containerList: {
      marginTop: theme.spacing.xxl,
      paddingHorizontal: theme.spacing.sm,
    },
    containerSmall: {
      alignItems: 'flex-end',
    },
    buttonsContainer: {
      flexDirection: 'row',
      justifyContent: 'center',
      gap: theme.spacing.xxl,
      flexWrap: 'wrap',
    },
    buttonsContainerList: {
      gap: theme.spacing.md,
    },
    buttonsContainerSmall: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: theme.spacing.sm,
      flexWrap: 'wrap',
    },
    socialButton: {
      width: 40,
      height: 40,
      justifyContent: 'center',
      alignItems: 'center',
    },
    socialButtonList: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing.sm,
      paddingBottom: theme.spacing.md,
      borderBottomWidth: 1,
      borderBottomColor: theme.colors.grey20,
    },
    socialButtonSmall: {
      width: 20,
      height: 20,
      justifyContent: 'center',
      alignItems: 'center',
    },
  })
